import { useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { CHART_THEME, CLASS_COLOUR_MAP, CLASS_LABELS, ASSET_CLASSES, LIABILITY_CLASSES } from 'utilities/global.variables';
import { formatCurrency, formatCurrencyCompact } from 'utilities/global.functions';

const filterByPeriod = (allData, period) => {
  const today = new Date();
  const toStr = d => d.toISOString().split('T')[0];
  if (period === '1m') {
    const cutoff = new Date(today); cutoff.setDate(today.getDate() - 30);
    return allData.filter(e => e.period >= toStr(cutoff));
  }
  if (period === 'ytd') {
    return allData.filter(e => e.period >= `${today.getFullYear()}-01-01`);
  }
  if (period === '1y') {
    const cutoff = new Date(today); cutoff.setFullYear(today.getFullYear() - 1);
    return allData.filter(e => e.period >= toStr(cutoff));
  }
  return allData; // 'all'
};

const HistoricLineGraphCard = ({ networthHistoric, period }) => {
  const { lineData, entryCount } = useMemo(() => {
    const entries = filterByPeriod(networthHistoric?.all || [], period);
    const toX = e => e.period;

    if (!entries.length) return { lineData: [], entryCount: 0 };

    const assetLines = ASSET_CLASSES.map(cls => ({
      id: cls,
      color: CLASS_COLOUR_MAP[cls],
      data: entries.map(e => ({ x: toX(e), y: e.classes[cls]?.total || 0 })),
    }));

    const liabilityLines = LIABILITY_CLASSES.map(cls => ({
      id: cls,
      color: CLASS_COLOUR_MAP[cls],
      data: entries.map(e => ({ x: toX(e), y: e.classes[cls]?.total || 0 })),
    }));

    const lineData = [...assetLines, ...liabilityLines]
      .filter(series => series.data.some(p => p.y !== 0));

    return { lineData, entryCount: entries.length };
  }, [networthHistoric, period]);

  const tickValues = period === '1m' ? 'every 1 week'
    : period === 'ytd' || period === '1y' ? 'every 1 month'
    : 'every 5 years';

  const { minValue, maxValue } = useMemo(() => {
    const allValues = lineData.flatMap(s => s.data.map(p => p.y));
    return {
      minValue: allValues.length ? Math.min(...allValues, 0) : 0,
      maxValue: allValues.length ? Math.max(...allValues, 0) : 0,
    };
  }, [lineData]);

  if (!lineData.length) return null;

  return (
    <div className="card r1-1 no-bg flex col large-gap">
      <div className="flex row space center">
        <p className="large">Portfolio ({period === "all" ? "All" : period.toUpperCase()})</p>
      </div>

      <div style={{width: "100%", height: "100%"}}>
        <ResponsiveLine
          data={lineData}
          theme={CHART_THEME}
          margin={{top: 40, right: 100, bottom: 10, left: 15}}
          xScale={{type: "time", format: "%Y-%m-%d", precision: "day"}}
          xFormat="time:%Y-%m-%d"
          yScale={{type: "linear", min: minValue, max: maxValue, clamp: true}}
       
          curve="monotoneX"
          enableSlices={false}
          axisBottom={null}
          axisLeft={null}
          axisTop={{
            tickSize: 0,
            tickPadding: 10,
            tickRotation: -90,
            tickValues,
            format: (value) => {
              const d = new Date(value);
              if (period === '1m') return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
              if (period === 'all') return d.toLocaleDateString('en-GB', { year: 'numeric' });
              return d.toLocaleDateString('en-GB', { month: 'short' });
            },
          }}
          axisRight={{
            tickSize: 0,
            tickPadding: 25,
            tickRotation: 0,
            tickValues: 4,
            format: formatCurrencyCompact
          }}
          enableArea={true}
          areaOpacity={0.2}
          gridXValues={tickValues}
          enableGridX={true}
          enableGridY={false}
          colors={{datum: "color"}}
          lineWidth={1.5}
          enablePoints={entryCount <= 30}
          pointSize={5}
          pointBorderColor="var(--d1)"
          pointBorderWidth={1}
          connectNullValues={false}
          useMesh={true}
          enableCrosshair={true}
          crosshairType="x"
          tooltip={({point}) => (
            <div className="Tooltip" style={{background: "var(--d3)", padding: "10px", color: "var(--l3)", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)", width: "max-content"}}>
              <div className="flex col medium-gap">
                <div className="flex row space large-gap">
                  <p className="regular">{CLASS_LABELS[point.seriesId] ?? point.seriesId}</p>
                  <p className="regular o60">{new Date(point.data.x).toLocaleDateString("en-GB", {day: "numeric", month: "short", year: "numeric"})}</p>
                </div>
                <p className="number xlarge">{formatCurrency(point.data.y)}</p>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default HistoricLineGraphCard;
